import {
  EnsoPhase,
  OniResponse,
  WeatherChartsResponse,
  YearlyMonthlyTotals,
} from 'base/types/FieldConditions';
import { getBrazilCropSeasonMonths } from 'utils/brazilCropSeason';

export interface RainfallPoint {
  label: string;
  rainfall: number | null;
  phase: EnsoPhase;
}

export function seasonalRainfall(
  weather: WeatherChartsResponse,
  seasonName?: string
): number | null {
  const months = getBrazilCropSeasonMonths(seasonName);
  if (!months) {
    return weather.precipitation.totalSeasonalRainfall;
  }
  const totals = months.map((month) => weather.precipitation.monthlyRainfall[month - 1]);
  return totals.every(Number.isFinite) ? totals.reduce((sum, total) => sum + total, 0) : null;
}

export function observedPhase(oni: OniResponse | null, year: number, month: number): EnsoPhase {
  const season = oni?.series.find((entry) => entry.year === year)?.seasons[month - 1];
  return season?.basis === 'observed' ? season.phase : 'unknown';
}

function completeMonth(entry: YearlyMonthlyTotals | undefined, month: number): number | null {
  if (!entry) {
    return null;
  }
  const total = entry.sums[month - 1];
  const expectedDays = new Date(Date.UTC(entry.year, month, 0)).getUTCDate();
  return entry.days[month - 1] >= expectedDays && Number.isFinite(total) ? total : null;
}

export function yearlyRainfall(
  weather: WeatherChartsResponse,
  oni: OniResponse | null
): RainfallPoint[] {
  const history = weather.yearly?.precipitation ?? [];
  const months = weather.season.months;
  if (!months.length) {
    return [];
  }
  return history
    .map((entry) => {
      const totals = months.map((month) => {
        const year = entry.year + (month < months[0] ? 1 : 0);
        return completeMonth(
          history.find((candidate) => candidate.year === year),
          month
        );
      });
      return {
        label: String(entry.year),
        rainfall: totals.every((total) => total !== null)
          ? totals.reduce<number>((sum, total) => sum + (total ?? 0), 0)
          : null,
        phase: observedPhase(oni, entry.year, months[0]),
      };
    })
    .filter((point) => point.rainfall !== null);
}

export function phaseMonthlyRainfall(weather: WeatherChartsResponse, oni: OniResponse | null) {
  const phases: EnsoPhase[] = ['la_nina', 'neutral', 'el_nino'];
  return phases.map((phase) => ({
    phase,
    values: weather.labels.map((label, index) => {
      const totals = (weather.yearly?.precipitation ?? [])
        .filter((entry) => observedPhase(oni, entry.year, index + 1) === phase)
        .map((entry) => completeMonth(entry, index + 1))
        .filter((total): total is number => total !== null);
      return {
        label,
        rainfall: totals.length
          ? totals.reduce((sum, total) => sum + total, 0) / totals.length
          : null,
      };
    }),
  }));
}
